import { TRACE_LINKS_FIRST } from '@/constant/pagination';

// Pure geometry for the index-driven segment ribbon.
//
// The ribbon virtualizes `totalCount` items with @tanstack/react-virtual. A
// trace's links have contiguous, 1-indexed heights (verified against the live
// backend: totalCount === head height, heights are exactly {1..totalCount},
// with no duplicates). We render them oldest → newest (left → right), so the
// three quantities below are exact bijections:
//
//   virtual index i:  0 = oldest link, totalCount-1 = head (newest)
//   height h:         1 = oldest link, totalCount   = head
//   GraphQL offset o: 0 = head, totalCount-1 = oldest link (height-DESC list)
//
//   height = index + 1
//   offset = (totalCount - 1) - index
//
// Because the mapping is exact, the visible virtual-index range maps directly
// to a single offset fetch — no cursors, no estimated-width spacers, no
// scroll-compensation. Unloaded indices simply render skeletons until their
// page arrives, so non-adjacent windows (a hard scroll / scrollbar-jump) are
// first-class rather than a special case.

export const indexToHeight = (index: number): number => index + 1;

export const heightToIndex = (height: number): number => height - 1;

export const indexToOffset = (index: number, totalCount: number): number =>
  totalCount - 1 - index;

export const offsetToIndex = (offset: number, totalCount: number): number =>
  totalCount - 1 - offset;

type FetchWindowInput = {
  // Inclusive virtual-index range currently in view (from the virtualizer).
  startIndex: number;
  endIndex: number;
  // True when the link at a given virtual index is already in the loaded window.
  isLoaded: (index: number) => boolean;
  totalCount: number;
  // Extra indices to eagerly load on each side of the visible range.
  overscan: number;
  // Minimum links to request per fetch (widened to span a larger gap).
  pageSize: number;
};

export type FetchWindow = { offset: number; first: number };

// Given the visible range (plus overscan), return the single offset-anchored
// page that covers the currently-unloaded indices, or null when the buffered
// range is already fully loaded (or there is nothing to virtualize).
//
// The returned `offset` is the SMALLEST offset spanning the missing range (the
// highest missing index, nearest the head), and `first` is wide enough to reach
// the lowest missing index — floored at `pageSize` so small scrolls still pull
// a useful page. offsets grow toward the origin, so
//   offset(maxMissing) .. offset(maxMissing)+first-1
// covers indices maxMissing .. minMissing.
export const computeFetchWindow = ({
  startIndex,
  endIndex,
  isLoaded,
  totalCount,
  overscan,
  pageSize
}: FetchWindowInput): FetchWindow | null => {
  if (totalCount <= 0) {
    return null;
  }

  const from = Math.max(0, startIndex - overscan);
  const to = Math.min(totalCount - 1, endIndex + overscan);

  let minMissing = Number.POSITIVE_INFINITY;
  let maxMissing = Number.NEGATIVE_INFINITY;
  for (let i = from; i <= to; i += 1) {
    if (!isLoaded(i)) {
      if (i < minMissing) {
        minMissing = i;
      }
      if (i > maxMissing) {
        maxMissing = i;
      }
    }
  }

  // Every index in the buffered range is already loaded → nothing to fetch.
  if (maxMissing < minMissing) {
    return null;
  }

  const offset = indexToOffset(maxMissing, totalCount);
  const span = maxMissing - minMissing + 1;
  const first = Math.max(pageSize, span);

  return { offset: Math.max(0, offset), first };
};

// Default page size the ribbon requests, shared with the pagination layer so
// both agree on one source of truth.
export const RIBBON_PAGE_SIZE = TRACE_LINKS_FIRST;
